import type { POI, Vec3 } from '../types';
import type { FloorPlanCal, FloorDef } from '../services/floorPlanService';
import { t } from '../i18n';
import { escapeHtml } from '../utils/html';
import { logEvent } from '../services/debugLog';

/**
 * 2D top-down map v2 (2026-07-12, user-approved mockup) — floor-plan-FIRST:
 * when a calibrated plan image exists for the visible floor it IS the map
 * (light "paper map" theme, crisp at full opacity); the walkable navmesh
 * becomes an optional 🕸 overlay. Venues without plans keep the original
 * dark navmesh rendering as fallback.
 *
 * Interactions: drag to pan, pinch/wheel/double-tap/＋－ to zoom, 📍
 * recenter, pins stay constant size (labels auto-hide when zoomed out),
 * tap a pin → bottom card with distance + Navigate (visitor mode) or
 * direct select (Dashboard mode). Route renders as white casing + animated
 * blue dashes; the user is a blue dot with heading cone + pulse ring.
 *
 * Used in two places:
 *  - Dashboard: 2D/3D toggle (tap POI = select, tap floor = draft position,
 *    plus 2-point plan calibration via calibTap).
 *  - Visitor: 🗺 map view — AR fallback and live in-AR overview.
 */
export interface Map2DUser {
  x: number;
  y?: number;
  z: number;
  headingRad?: number;
}

/** A detected floor: an elevation band of the walkable surface. */
interface FloorBand {
  minY: number;
  maxY: number;
  label: string;
}

/** A point in floor-plan IMAGE pixels (u→right, v→down). */
export interface CalibPoint {
  u: number;
  v: number;
}

const ZOOM_MIN = 0.35;
const ZOOM_MAX = 9;

export class Map2D {
  readonly el: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private floorsEl: HTMLDivElement;
  private sheetEl: HTMLDivElement | null = null;
  private hintEl: HTMLDivElement;
  private scaleTxt: HTMLSpanElement;
  private meshBtn: HTMLButtonElement;
  private tris: { positions: number[]; indices: number[] } | null = null;
  private pois: POI[] = [];
  private user: Map2DUser | null = null;
  private route: Vec3[] | null = null;
  /** No-route fallback: dashed straight hint line user → destination. */
  private hint: Vec3[] | null = null;
  private b = { minX: -5, maxX: 5, minZ: -5, maxZ: 5 };
  private bands: FloorBand[] = [];
  private activeBand = 0;
  /** Draft POI position (Dashboard Add-POI via 2D tap). */
  private draft: { x: number; z: number } | null = null;
  /** Architectural floor-plan underlays (per floor band) + loaded images. */
  private plans: FloorPlanCal[] = [];
  private planImgs = new Map<string, HTMLImageElement>();
  /** Explicit floors (Dashboard-configured) — overrides auto-detection,
   *  which is defeated by wall-mounted POIs bridging floor elevations
   *  (real data 2026-07-12: POIs at y 1.95/3.20 chained floor 0 → floor 2). */
  private explicitFloors: FloorDef[] = [];
  /** User view transform on top of the fit-to-floor base transform. */
  private zoom = 1;
  private panX = 0;
  private panY = 0;
  /** 🕸 walkable overlay on top of a plan (always drawn when no plan). */
  private meshOn = false;
  private selected: POI | null = null;
  /** Animation loop (route dashes + user pulse) — runs only while visible. */
  private active = false;
  private raf = 0;
  private phase = 0;
  private lastT = 0;
  private needsDraw = true;
  /** Dashboard hook: fires when the visible floor changes (dropdown or
   *  auto-follow) so the floor-plan editor can retarget its fields. */
  onFloorChange?: (band: number) => void;
  /** Calibration mode (Dashboard): when set, taps are captured and reported
   *  as (world x/z, plan-image pixel or null when no plan is drawn). */
  calibTap: ((world: { x: number; z: number }, img: CalibPoint | null) => void) | null = null;
  private calibMarkers: Array<{ x: number; z: number; label: string }> = [];
  // gesture state
  private ptrs = new Map<number, { x: number; y: number; sx: number; sy: number }>();
  private pinchDist = 0;
  private moved = false;
  private lastTapAt = 0;

  /** Dashboard hook: a POI pin was DRAGGED to a new spot (save it). */
  onPoiMove?: (poi: POI, x: number, z: number) => void;
  /** POI currently being dragged (movable mode). */
  private dragPoi: POI | null = null;

  constructor(
    private onPoiTap?: (poi: POI) => void,
    private onEmptyTap?: (x: number, z: number, floorY: number) => void,
    private opts: { sheet?: boolean; movable?: boolean } = {},
  ) {
    this.el = document.createElement('div');
    this.el.className = 'map2d';
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'map2d-canvas';
    this.floorsEl = document.createElement('div');
    this.floorsEl.className = 'map2d-floors';
    this.el.append(this.canvas, this.floorsEl);
    this.buildChrome();
    this.hintEl = document.createElement('div');
    this.hintEl.className = 'map2d-hint hidden';
    this.hintEl.textContent = t('map2d.hint');
    this.el.appendChild(this.hintEl);
    this.scaleTxt = this.el.querySelector('.map2d-scale span')!;
    this.meshBtn = this.el.querySelector('.map2d-mesh')!;
    this.bindGestures();
    new ResizeObserver(() => this.invalidate()).observe(this.el);
  }

  // ---------- chrome (zoom / recenter / mesh / scale / north / sheet) ----------

  private buildChrome(): void {
    const col = document.createElement('div');
    col.className = 'map2d-ctrl';
    const mk = (cls: string, txt: string, title: string, fn: () => void) => {
      const b = document.createElement('button');
      b.className = `map2d-fab ${cls}`;
      b.textContent = txt;
      b.title = title;
      b.addEventListener('click', fn);
      col.appendChild(b);
      return b;
    };
    mk('', '＋', 'Zoom in', () => this.zoomAt(this.el.clientWidth / 2, this.el.clientHeight / 2, 1.4));
    mk('', '－', 'Zoom out', () => this.zoomAt(this.el.clientWidth / 2, this.el.clientHeight / 2, 1 / 1.4));
    mk('', '📍', 'Recenter', () => this.recenter());
    mk('map2d-mesh', '🕸', 'Walkable-area overlay', () => {
      this.meshOn = !this.meshOn;
      this.meshBtn.classList.toggle('on', this.meshOn);
      this.invalidate();
    });
    this.el.appendChild(col);

    const north = document.createElement('div');
    north.className = 'map2d-north';
    north.textContent = 'N';
    this.el.appendChild(north);

    const sb = document.createElement('div');
    sb.className = 'map2d-scale';
    sb.innerHTML = '<span>5 m</span><div class="map2d-scale-bar"></div>';
    this.el.appendChild(sb);

    if (this.opts.sheet) {
      const sh = document.createElement('div');
      sh.className = 'map2d-sheet hidden';
      sh.innerHTML = `
        <div class="map2d-sheet-row">
          <div class="map2d-sheet-icon"></div>
          <div class="map2d-sheet-text"><h2></h2><p></p></div>
        </div>
        <div class="map2d-sheet-btns">
          <button class="btn primary map2d-go"></button>
          <button class="btn ghost map2d-dismiss"></button>
        </div>`;
      (sh.querySelector('.map2d-go') as HTMLButtonElement).addEventListener('click', () => {
        const poi = this.selected;
        this.closeSheet();
        if (poi) this.onPoiTap?.(poi);
      });
      (sh.querySelector('.map2d-dismiss') as HTMLButtonElement).addEventListener('click', () => this.closeSheet());
      this.el.appendChild(sh);
      this.sheetEl = sh;
    }
  }

  private openSheet(poi: POI): void {
    if (!this.sheetEl) return;
    this.selected = poi;
    (this.sheetEl.querySelector('.map2d-sheet-icon') as HTMLElement).textContent = poi.icon ?? '📍';
    (this.sheetEl.querySelector('h2') as HTMLElement).textContent = poi.name;
    const parts: string[] = [escapeHtml(poi.category)];
    if (this.user) {
      const d = Math.hypot(poi.position.x - this.user.x, poi.position.z - this.user.z);
      parts.push(escapeHtml(t('map2d.away', { d: String(Math.max(1, Math.round(d))) })));
    }
    const band = this.bands[this.activeBand];
    if (band && this.bands.length > 1) parts.push(escapeHtml(band.label));
    (this.sheetEl.querySelector('p') as HTMLElement).innerHTML = parts.join(' · ');
    (this.sheetEl.querySelector('.map2d-go') as HTMLButtonElement).textContent = t('map2d.navigate');
    (this.sheetEl.querySelector('.map2d-dismiss') as HTMLButtonElement).textContent = t('map2d.close');
    this.sheetEl.classList.remove('hidden');
    this.invalidate();
  }

  private closeSheet(): void {
    this.sheetEl?.classList.add('hidden');
    this.selected = null;
    this.invalidate();
  }

  /** Start/stop the animation loop — call when the map becomes (in)visible. */
  setActive(on: boolean): void {
    if (this.active === on) return;
    this.active = on;
    if (on) {
      this.lastT = performance.now();
      const loop = (now: number) => {
        if (!this.active) return;
        const dt = now - this.lastT;
        this.lastT = now;
        // Animate only when something moves (route dashes / pulse / draft).
        if (this.route || this.user || this.draft || this.hint) {
          this.phase = (this.phase + dt * 0.02) % 1000;
          this.needsDraw = true;
        }
        if (this.needsDraw) {
          this.needsDraw = false;
          this.render();
        }
        this.raf = requestAnimationFrame(loop);
      };
      this.raf = requestAnimationFrame(loop);
      if (this.opts.sheet) {
        this.hintEl.classList.remove('hidden');
        window.setTimeout(() => this.hintEl.classList.add('hidden'), 3200);
      }
    } else {
      cancelAnimationFrame(this.raf);
      this.closeSheet();
    }
    this.invalidate();
  }

  private invalidate(): void {
    this.needsDraw = true;
    if (!this.active) this.render(); // no loop running — draw once now
  }

  // ---------- gestures ----------

  private bindGestures(): void {
    const cv = this.canvas;
    cv.style.touchAction = 'none';
    cv.addEventListener('pointerdown', (ev) => {
      cv.setPointerCapture(ev.pointerId);
      this.ptrs.set(ev.pointerId, { x: ev.clientX, y: ev.clientY, sx: ev.clientX, sy: ev.clientY });
      if (this.ptrs.size === 1) {
        this.moved = false;
        // Dashboard: press ON a pin starts a POI drag (move-to-save, like
        // the 3D gizmo) instead of panning the map.
        if (this.opts.movable && !this.calibTap) {
          const rect = cv.getBoundingClientRect();
          this.dragPoi = this.hitPoi(ev.clientX - rect.left, ev.clientY - rect.top);
        }
      }
      this.pinchDist = 0;
    });
    cv.addEventListener('pointermove', (ev) => {
      const p = this.ptrs.get(ev.pointerId);
      if (!p) return;
      const prev = { x: p.x, y: p.y };
      p.x = ev.clientX;
      p.y = ev.clientY;
      if (Math.hypot(p.x - p.sx, p.y - p.sy) > 7) this.moved = true;
      if (this.ptrs.size === 1 && this.dragPoi && this.moved) {
        // Drag the pin, not the map.
        const rect = cv.getBoundingClientRect();
        const [mx, mz] = this.mapFromPx(ev.clientX - rect.left, ev.clientY - rect.top);
        this.dragPoi.position.x = mx;
        this.dragPoi.position.z = mz;
        this.invalidate();
      } else if (this.ptrs.size === 1) {
        this.panX += p.x - prev.x;
        this.panY += p.y - prev.y;
        this.invalidate();
      } else if (this.ptrs.size === 2) {
        const [a, b] = [...this.ptrs.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (this.pinchDist > 0) {
          const rect = cv.getBoundingClientRect();
          this.zoomAt((a.x + b.x) / 2 - rect.left, (a.y + b.y) / 2 - rect.top, d / this.pinchDist);
        }
        this.pinchDist = d;
      }
    });
    const end = (ev: PointerEvent) => {
      const p = this.ptrs.get(ev.pointerId);
      this.ptrs.delete(ev.pointerId);
      if (this.ptrs.size < 2) this.pinchDist = 0;
      // Finish a POI drag: report the new spot for saving.
      if (this.dragPoi) {
        const poi = this.dragPoi;
        this.dragPoi = null;
        if (this.moved) {
          this.onPoiMove?.(poi, poi.position.x, poi.position.z);
          return;
        }
      }
      if (!p || this.moved || this.ptrs.size > 0) return;
      const rect = cv.getBoundingClientRect();
      const px = ev.clientX - rect.left;
      const py = ev.clientY - rect.top;
      const now = Date.now();
      if (now - this.lastTapAt < 300) {
        this.lastTapAt = 0;
        this.zoomAt(px, py, 1.7); // double-tap zoom
        return;
      }
      this.lastTapAt = now;
      this.handleTap(px, py);
    };
    cv.addEventListener('pointerup', end);
    cv.addEventListener('pointercancel', (ev) => this.ptrs.delete(ev.pointerId));
    cv.addEventListener(
      'wheel',
      (ev) => {
        ev.preventDefault();
        const rect = cv.getBoundingClientRect();
        this.zoomAt(ev.clientX - rect.left, ev.clientY - rect.top, ev.deltaY < 0 ? 1.12 : 1 / 1.12);
      },
      { passive: false },
    );
  }

  private zoomAt(cx: number, cy: number, f: number): void {
    const nz = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, this.zoom * f));
    const k = nz / this.zoom;
    this.panX = cx - (cx - this.panX) * k;
    this.panY = cy - (cy - this.panY) * k;
    this.zoom = nz;
    this.invalidate();
  }

  private recenter(): void {
    if (this.user) {
      this.zoom = Math.max(this.zoom, 2);
      const { s, ox, oz } = this.base();
      this.panX = this.el.clientWidth / 2 - (ox + (this.user.x - this.b.minX) * s) * this.zoom;
      this.panY = this.el.clientHeight / 2 - (oz + (this.user.z - this.b.minZ) * s) * this.zoom;
    } else {
      this.zoom = 1;
      this.panX = 0;
      this.panY = 0;
    }
    this.invalidate();
  }

  /** POI pin under a canvas point (bubble center is 26 css-px above anchor). */
  private hitPoi(px: number, py: number): POI | null {
    let best: { poi: POI; d: number } | null = null;
    for (const poi of this.pois) {
      if (!this.inBand(poi.position.y)) continue;
      const [x, y] = this.pxFromMap(poi.position.x, poi.position.z);
      const d = Math.min(Math.hypot(x - px, y - py), Math.hypot(x - px, y - 26 - py));
      if (d < 24 && (!best || d < best.d)) best = { poi, d };
    }
    return best?.poi ?? null;
  }

  private handleTap(px: number, py: number): void {
    // Calibration mode captures every tap (Dashboard 2-point alignment).
    if (this.calibTap) {
      const [mx, mz] = this.mapFromPx(px, py);
      this.calibTap({ x: mx, z: mz }, this.imgPxFromMap(mx, mz));
      return;
    }
    const hit = this.hitPoi(px, py);
    if (hit) {
      const best = { poi: hit };
      if (this.opts.sheet) this.openSheet(best.poi);
      else {
        this.selected = best.poi;
        this.invalidate();
        this.onPoiTap?.(best.poi);
      }
    } else {
      if (this.sheetEl && !this.sheetEl.classList.contains('hidden')) {
        this.closeSheet();
        return;
      }
      const [mx, mz] = this.mapFromPx(px, py);
      this.onEmptyTap?.(mx, mz, this.activeFloorY());
    }
  }

  // ---------- floors ----------

  /** Base elevation of the currently shown floor (for placing new POIs). */
  activeFloorY(): number {
    return this.bands[this.activeBand]?.minY ?? 0;
  }

  /** Index of the visible floor band (lowest elevation = 0). */
  get band(): number {
    return this.activeBand;
  }

  /** World-space bounds of the visible floor (for default plan calibration). */
  bounds(): { minX: number; maxX: number; minZ: number; maxZ: number } {
    return { ...this.b };
  }

  /** Elevation range of the visible floor band (for the plan generator). */
  bandRange(): { minY: number; maxY: number } {
    const b = this.bands[this.activeBand];
    return b ? { minY: b.minY, maxY: b.maxY } : { minY: 0, maxY: 0 };
  }

  private inBand(y: number, pad = 0.8): boolean {
    const band = this.bands[this.activeBand];
    if (!band) return true;
    return y >= band.minY - pad && y <= band.maxY + pad;
  }

  /** Cluster walkable-surface elevations into floors (gap > 1.4 m = new floor). */
  private detectFloors(): void {
    // EXPLICIT floors win: band i spans [base−0.6 … nextBase−0.7] (last one
    // +3.5 m headroom) — deterministic, immune to odd POI elevations.
    if (this.explicitFloors.length > 0) {
      const sorted = [...this.explicitFloors].sort((a, b) => a.y - b.y);
      this.bands = sorted.map((f, i) => ({
        minY: f.y - 0.6,
        maxY: sorted[i + 1] ? sorted[i + 1].y - 0.7 : f.y + 3.5,
        label: f.label || `Floor ${i + 1}`,
      }));
      this.activeBand = Math.min(this.activeBand, this.bands.length - 1);
      logEvent(
        'session',
        `2D floors (explicit): ${this.bands.map((b) => `${b.label}[${b.minY.toFixed(1)}…${b.maxY.toFixed(1)}m]`).join(' ')}`,
      );
      this.renderFloorChips();
      return;
    }
    this.bands = [];
    const ys: number[] = [];
    if (this.tris) {
      const { positions, indices } = this.tris;
      for (let i = 0; i < indices.length; i += 3) {
        ys.push(
          (positions[indices[i] * 3 + 1] + positions[indices[i + 1] * 3 + 1] + positions[indices[i + 2] * 3 + 1]) / 3,
        );
      }
    } else {
      for (const p of this.pois) ys.push(p.position.y);
    }
    ys.sort((a, b) => a - b);
    for (const y of ys) {
      const cur = this.bands[this.bands.length - 1];
      if (cur && y - cur.maxY <= 1.4) cur.maxY = Math.max(cur.maxY, y);
      else this.bands.push({ minY: y, maxY: y, label: '' });
    }
    // FLOOR-DETECTION FIX (2026-07-12, user: "don't see multi floor
    // option"): the walkable mesh alone missed floors whose scan produced no
    // walkable surface (e.g. an upstairs area recast rejected). POIs know
    // better — cluster any POI elevations OUTSIDE the walkable bands into
    // additional floors so they're viewable/editable in 2D.
    const orphanYs = this.pois
      .map((p) => p.position.y)
      .filter((y) => !this.bands.some((b) => y >= b.minY - 0.8 && y <= b.maxY + 0.8))
      .sort((a, b) => a - b);
    let cluster: FloorBand | null = null;
    for (const y of orphanYs) {
      if (cluster && y - cluster.maxY <= 1.4) cluster.maxY = y;
      else {
        cluster = { minY: y, maxY: y, label: '' };
        this.bands.push(cluster);
      }
    }
    this.bands.sort((a, b) => a.minY - b.minY);
    // Label each band by the dominant POI "floor" number inside it.
    this.bands.forEach((band, i) => {
      const counts = new Map<number, number>();
      for (const p of this.pois) {
        if (p.position.y >= band.minY - 0.8 && p.position.y <= band.maxY + 0.8) {
          counts.set(p.floor, (counts.get(p.floor) ?? 0) + 1);
        }
      }
      const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
      band.label = top ? `Floor ${top[0]}` : `Level ${i + 1}`;
    });
    this.activeBand = Math.min(this.activeBand, Math.max(0, this.bands.length - 1));
    logEvent(
      'session',
      `2D floors detected: ${this.bands.length} — ${this.bands.map((b) => `${b.label}[${b.minY.toFixed(1)}…${b.maxY.toFixed(1)}m]`).join(' ')}`,
    );
    this.renderFloorChips();
  }

  /** Floor DROPDOWN (user spec) — shown only for multi-floor venues. */
  private renderFloorChips(): void {
    this.floorsEl.innerHTML = '';
    if (this.bands.length <= 1) return;
    const sel = document.createElement('select');
    sel.className = 'map2d-floor-select';
    this.bands.forEach((band, i) => {
      const o = document.createElement('option');
      o.value = String(i);
      o.textContent = band.label;
      o.selected = i === this.activeBand;
      sel.appendChild(o);
    });
    sel.addEventListener('change', () => {
      this.activeBand = Number(sel.value);
      this.resetView();
      this.onFloorChange?.(this.activeBand);
    });
    this.floorsEl.appendChild(sel);
  }

  private resetView(): void {
    this.fitBounds();
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.closeSheet();
    this.invalidate();
  }

  // ---------- data ----------

  /** Show/clear the amber draft marker (Add POI via 2D tap). */
  setDraft(x: number | null, z?: number): void {
    this.draft = x === null || z === undefined ? null : { x, z };
    this.invalidate();
  }

  /** Navmesh triangles (getNavmeshDebugData()) + POI catalog. */
  setData(tris: { positions: number[]; indices: number[] } | null, pois: POI[]): void {
    this.tris = tris;
    this.pois = pois;
    this.detectFloors();
    this.fitBounds();
    this.invalidate();
  }

  /** Explicit floors (empty = auto-detect). Re-clusters immediately. */
  setExplicitFloors(floors: FloorDef[]): void {
    this.explicitFloors = floors;
    this.detectFloors();
    this.fitBounds();
    this.invalidate();
  }

  /** Set/replace the architectural floor-plan underlays. */
  setFloorPlans(plans: FloorPlanCal[]): void {
    this.plans = plans;
    for (const p of plans) {
      if (this.planImgs.has(p.url)) continue;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => this.invalidate();
      img.src = p.url;
      this.planImgs.set(p.url, img);
    }
    this.invalidate();
  }

  /** Calibration helpers (Dashboard): numbered markers for picked points. */
  addCalibMarker(x: number, z: number, label: string): void {
    this.calibMarkers.push({ x, z, label });
    this.invalidate();
  }
  clearCalibMarkers(): void {
    this.calibMarkers = [];
    this.invalidate();
  }
  /** Force the walkable overlay on/off (calibration needs it visible). */
  setMeshOverlay(on: boolean): void {
    this.meshOn = on;
    this.meshBtn.classList.toggle('on', on);
    this.invalidate();
  }

  /** Bounds of the ACTIVE floor's walkable surface (POIs as fallback). */
  private fitBounds(): void {
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    if (this.tris && this.tris.positions.length >= 3) {
      const { positions, indices } = this.tris;
      for (let i = 0; i < indices.length; i += 3) {
        const cy =
          (positions[indices[i] * 3 + 1] + positions[indices[i + 1] * 3 + 1] + positions[indices[i + 2] * 3 + 1]) / 3;
        if (!this.inBand(cy)) continue;
        for (const vi of [indices[i], indices[i + 1], indices[i + 2]]) {
          minX = Math.min(minX, positions[vi * 3]);
          maxX = Math.max(maxX, positions[vi * 3]);
          minZ = Math.min(minZ, positions[vi * 3 + 2]);
          maxZ = Math.max(maxZ, positions[vi * 3 + 2]);
        }
      }
    }
    if (!Number.isFinite(minX)) {
      // POI-only floor (no walkable tris in this band) — fit ITS POIs.
      for (const p of this.pois) {
        if (!this.inBand(p.position.y)) continue;
        minX = Math.min(minX, p.position.x);
        maxX = Math.max(maxX, p.position.x);
        minZ = Math.min(minZ, p.position.z);
        maxZ = Math.max(maxZ, p.position.z);
      }
    }
    if (Number.isFinite(minX) && maxX > minX) this.b = { minX: minX - 1, maxX: maxX + 1, minZ: minZ - 1, maxZ: maxZ + 1 };
  }

  /** Live overlay: user position/heading + active route polyline. `hint`
   *  is the no-route fallback — a dashed straight line to the destination
   *  so the map still answers "which way?" when routing failed. */
  setLive(user: Map2DUser | null, route: Vec3[] | null, hint: Vec3[] | null = null): void {
    this.user = user;
    this.route = route;
    this.hint = hint;
    // Follow the visitor's floor: if their elevation belongs to another
    // band, switch to it automatically.
    if (user?.y !== undefined && this.bands.length > 1 && !this.inBand(user.y - 1.4 /* eye→floor */)) {
      const fy = user.y - 1.4;
      const idx = this.bands.findIndex((b) => fy >= b.minY - 0.9 && fy <= b.maxY + 0.9);
      if (idx >= 0 && idx !== this.activeBand) {
        this.activeBand = idx;
        this.fitBounds();
        this.renderFloorChips();
        this.onFloorChange?.(this.activeBand);
      }
    }
    this.invalidate();
  }

  // ---------- transforms ----------

  /** Cached fit transform — recomputed once per render (base() reads layout;
   *  calling it per-triangle would hammer clientWidth thousands of times). */
  private vw = { s: 1, ox: 0, oz: 0 };

  /** Fit-to-floor base transform (world meters → css px at zoom 1). */
  private base(): { s: number; ox: number; oz: number; w: number; h: number } {
    const w = this.el.clientWidth || 300;
    const h = this.el.clientHeight || 300;
    const pad = 24;
    const dx = this.b.maxX - this.b.minX;
    const dz = this.b.maxZ - this.b.minZ;
    const s = Math.min((w - pad * 2) / dx, (h - pad * 2) / dz);
    return { s, ox: (w - dx * s) / 2, oz: (h - dz * s) / 2, w, h };
  }

  private pxFromMap(x: number, z: number): [number, number] {
    const { s, ox, oz } = this.vw;
    return [
      (ox + (x - this.b.minX) * s) * this.zoom + this.panX,
      (oz + (z - this.b.minZ) * s) * this.zoom + this.panY,
    ];
  }

  private mapFromPx(px: number, py: number): [number, number] {
    const { s, ox, oz } = this.vw;
    return [
      this.b.minX + ((px - this.panX) / this.zoom - ox) / s,
      this.b.minZ + ((py - this.panY) / this.zoom - oz) / s,
    ];
  }

  /** css px per world meter at the current zoom. */
  private ppm(): number {
    return this.vw.s * this.zoom;
  }

  private activePlan(): { plan: FloorPlanCal; img: HTMLImageElement } | null {
    const plan = this.plans.find((p) => p.band === this.activeBand);
    if (!plan) return null;
    const img = this.planImgs.get(plan.url);
    if (!img || !img.complete || img.naturalWidth === 0) return null;
    return { plan, img };
  }

  /** World (x,z) → plan-image pixel, or null when this floor has no plan. */
  private imgPxFromMap(x: number, z: number): CalibPoint | null {
    const ap = this.activePlan();
    if (!ap) return null;
    const { plan } = ap;
    const r = (plan.rotDeg * Math.PI) / 180;
    const dx = x - plan.x;
    const dz = z - plan.z;
    return {
      u: (dx * Math.cos(r) + dz * Math.sin(r)) / plan.mpp,
      v: (-dx * Math.sin(r) + dz * Math.cos(r)) / plan.mpp,
    };
  }

  // ---------- rendering ----------

  render(): void {
    const { s, ox, oz, w, h } = this.base();
    this.vw = { s, ox, oz };
    const dpr = window.devicePixelRatio || 1;
    if (this.canvas.width !== w * dpr || this.canvas.height !== h * dpr) {
      this.canvas.width = w * dpr;
      this.canvas.height = h * dpr;
      this.canvas.style.width = `${w}px`;
      this.canvas.style.height = `${h}px`;
    }
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const ap = this.activePlan();
    const light = ap !== null; // paper-map theme when a plan is the base map
    ctx.fillStyle = light ? '#eef2f7' : '#0b1224';
    ctx.fillRect(0, 0, w, h);

    // 1) Floor-plan image — THE map when present (full opacity, shadow).
    if (ap) {
      const { plan, img } = ap;
      const [px0, py0] = this.pxFromMap(plan.x, plan.z);
      ctx.save();
      ctx.shadowColor = 'rgba(15, 23, 42, 0.18)';
      ctx.shadowBlur = 18;
      ctx.globalAlpha = plan.opacity ?? 1;
      ctx.translate(px0, py0);
      ctx.rotate((plan.rotDeg * Math.PI) / 180);
      const k = plan.mpp * this.ppm(); // image px → meters → screen px
      ctx.scale(k, k);
      ctx.drawImage(img, 0, 0);
      ctx.restore();
    }

    // 2) Walkable navmesh — the base map when no plan; optional 🕸 overlay
    //    on top of a plan (verifies alignment, shows walkability).
    this.meshBtn.classList.toggle('hidden', !ap);
    if (this.tris && (!ap || this.meshOn)) this.drawMesh(ctx, light);
    else if (!this.tris && !ap) {
      ctx.fillStyle = light ? '#64748b' : '#94a3b8';
      ctx.font = '13px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('Floor plan loads once the map data is ready…', w / 2, h / 2);
    }

    // 3) Route: white casing + animated dashes. Cross-floor routes draw only
    //    the ACTIVE floor's portion — a stairwell descent flattened onto one
    //    floor would read as a phantom corridor through the building.
    const routePts = this.route?.filter((p) => this.inBand(p.y, 1.2)) ?? null;
    if (routePts && routePts.length >= 2) {
      const pts = routePts.map((p) => this.pxFromMap(p.x, p.z));
      const trace = () => {
        ctx.beginPath();
        pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      };
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = light ? 'rgba(255,255,255,0.95)' : 'rgba(8,14,28,0.85)';
      ctx.lineWidth = 11;
      trace();
      ctx.stroke();
      ctx.strokeStyle = '#0ea5e9';
      ctx.lineWidth = 6;
      ctx.setLineDash([2, 12]);
      ctx.lineDashOffset = -this.phase * 15;
      trace();
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // 3b) No-route hint: dashed amber line straight to the destination.
    //     Deliberately NOT band-filtered — it may cross floors, and that's
    //     exactly what it should communicate (destination pin shows the
    //     floor in its label; the venue needs a floor link for a real path).
    if (this.hint && this.hint.length >= 2) {
      const pts = this.hint.map((p) => this.pxFromMap(p.x, p.z));
      ctx.lineCap = 'round';
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.9)';
      ctx.lineWidth = 4;
      ctx.setLineDash([6, 10]);
      ctx.lineDashOffset = -this.phase * 10;
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      ctx.stroke();
      ctx.setLineDash([]);
      const [ex, ey] = pts[pts.length - 1];
      ctx.fillStyle = '#f59e0b';
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(ex, ey, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      // HONEST FLOOR LABEL (user 2026-07-12: "point is on the floor below
      // but it was showing like current floor") — when the destination is on
      // another floor, say so right at the dot: "Floor 1 ⬇".
      const lastPt = this.hint[this.hint.length - 1];
      const destBand = this.bands.findIndex(
        (bd) => lastPt.y >= bd.minY - 0.9 && lastPt.y <= bd.maxY + 0.9,
      );
      if (destBand >= 0 && destBand !== this.activeBand) {
        const chip = `${this.bands[destBand].label} ${destBand < this.activeBand ? '⬇' : '⬆'}`;
        ctx.font = '700 12px system-ui';
        const tw = ctx.measureText(chip).width;
        ctx.fillStyle = '#f59e0b';
        ctx.beginPath();
        ctx.roundRect(ex - tw / 2 - 7, ey + 11, tw + 14, 20, 10);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.fillText(chip, ex, ey + 25);
      }
    }

    // 4) POI pins (active floor only) — constant size, labels hide when far.
    const showLabels = this.ppm() > 9;
    ctx.textAlign = 'center';
    for (const poi of this.pois) {
      if (!this.inBand(poi.position.y)) continue;
      const [x, y] = this.pxFromMap(poi.position.x, poi.position.z);
      if (x < -40 || y < -40 || x > w + 40 || y > h + 40) continue;
      this.drawPin(ctx, x, y, poi.icon ?? '📍', this.selected?.id === poi.id, light);
      if (showLabels) this.drawLabel(ctx, x, y + 6, poi.name, light);
    }

    // 5) Draft POI (Dashboard Add-POI tap) — amber pin.
    if (this.draft) {
      const [x, y] = this.pxFromMap(this.draft.x, this.draft.z);
      const pul = 8 + ((this.phase * 12) % 10);
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.5)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, pul, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = '#f59e0b';
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, Math.PI * 2);
      ctx.fill();
    }

    // 6) Calibration markers (numbered).
    for (const m of this.calibMarkers) {
      const [x, y] = this.pxFromMap(m.x, m.z);
      ctx.fillStyle = '#a855f7';
      ctx.beginPath();
      ctx.arc(x, y, 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11px system-ui';
      ctx.fillText(m.label, x, y + 4);
    }

    // 7) User: pulse ring + heading cone + dot.
    if (this.user) {
      const [x, y] = this.pxFromMap(this.user.x, this.user.z);
      const pk = (this.phase * 7) % 22;
      ctx.fillStyle = `rgba(14, 165, 233, ${Math.max(0, 0.4 - pk * 0.018)})`;
      ctx.beginPath();
      ctx.arc(x, y, 9 + pk, 0, Math.PI * 2);
      ctx.fill();
      if (this.user.headingRad !== undefined) {
        // heading convention: 0 = -Z, positive toward +X (poseTransform).
        const hr = this.user.headingRad;
        ctx.fillStyle = 'rgba(14, 165, 233, 0.30)';
        ctx.beginPath();
        ctx.moveTo(x, y);
        const spread = 0.45;
        ctx.lineTo(x + Math.sin(hr - spread) * 26, y - Math.cos(hr - spread) * 26);
        ctx.lineTo(x + Math.sin(hr + spread) * 26, y - Math.cos(hr + spread) * 26);
        ctx.closePath();
        ctx.fill();
      }
      ctx.fillStyle = '#0ea5e9';
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }

    // Scale bar: fixed 60 css px → meters.
    const meters = 60 / this.ppm();
    this.scaleTxt.textContent =
      meters >= 1 ? `${meters.toFixed(meters < 3 ? 1 : 0)} m` : `${Math.round(meters * 100)} cm`;
  }

  private drawMesh(ctx: CanvasRenderingContext2D, light: boolean): void {
    if (!this.tris) return;
    const { positions, indices } = this.tris;
    ctx.fillStyle = light ? 'rgba(14, 165, 233, 0.10)' : 'rgba(56, 189, 248, 0.14)';
    ctx.strokeStyle = light ? 'rgba(14, 165, 233, 0.08)' : 'rgba(56, 189, 248, 0.10)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    // Boundary-edge detection: edges used by exactly ONE triangle are the
    // outline of the walkable area — i.e. where floor meets walls or
    // furniture. Drawn bold, they read like a floor plan with borders.
    const edgeCount = new Map<string, [number, number]>();
    const seen = new Map<string, number>();
    for (let i = 0; i < indices.length; i += 3) {
      const a = indices[i] * 3, b = indices[i + 1] * 3, c = indices[i + 2] * 3;
      // Only the ACTIVE floor's triangles (multi-floor scans overlap in 2D).
      const centY = (positions[a + 1] + positions[b + 1] + positions[c + 1]) / 3;
      if (!this.inBand(centY)) continue;
      const [ax, ay] = this.pxFromMap(positions[a], positions[a + 2]);
      const [bx, by] = this.pxFromMap(positions[b], positions[b + 2]);
      const [cx, cy] = this.pxFromMap(positions[c], positions[c + 2]);
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
      ctx.lineTo(cx, cy);
      ctx.closePath();
      for (const [v1, v2] of [
        [indices[i], indices[i + 1]],
        [indices[i + 1], indices[i + 2]],
        [indices[i + 2], indices[i]],
      ]) {
        const key = v1 < v2 ? `${v1}:${v2}` : `${v2}:${v1}`;
        seen.set(key, (seen.get(key) ?? 0) + 1);
        if (!edgeCount.has(key)) edgeCount.set(key, [v1, v2]);
      }
    }
    ctx.fill();
    ctx.stroke();
    // Walls/borders: boundary edges, drawn bold.
    ctx.strokeStyle = light ? 'rgba(14, 165, 233, 0.55)' : 'rgba(148, 197, 255, 0.85)';
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    for (const [key, [v1, v2]] of edgeCount) {
      if (seen.get(key) !== 1) continue;
      const [x1, y1] = this.pxFromMap(positions[v1 * 3], positions[v1 * 3 + 2]);
      const [x2, y2] = this.pxFromMap(positions[v2 * 3], positions[v2 * 3 + 2]);
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
    }
    ctx.stroke();
  }

  /** Teardrop pin: circle bubble + tail, emoji inside, anchored at (x,y). */
  private drawPin(ctx: CanvasRenderingContext2D, x: number, y: number, icon: string, sel: boolean, light: boolean): void {
    const cy = y - 26; // bubble center
    ctx.save();
    ctx.shadowColor = 'rgba(15, 23, 42, 0.30)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 2;
    ctx.fillStyle = sel ? '#0ea5e9' : light ? '#ffffff' : '#1e293b';
    // tail
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - 7, cy + 11);
    ctx.lineTo(x + 7, cy + 11);
    ctx.closePath();
    ctx.fill();
    // bubble
    ctx.beginPath();
    ctx.arc(x, cy, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.strokeStyle = sel ? '#0284c7' : light ? 'rgba(15,23,42,0.15)' : 'rgba(148,197,255,0.4)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x, cy, 15, 0, Math.PI * 2);
    ctx.stroke();
    ctx.font = '15px system-ui';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(icon, x, cy + 1);
    ctx.textBaseline = 'alphabetic';
  }

  /** Name chip under the pin anchor. */
  private drawLabel(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, light: boolean): void {
    ctx.font = '600 11px system-ui';
    const tw = ctx.measureText(text).width;
    const bw = tw + 12;
    ctx.fillStyle = light ? 'rgba(255,255,255,0.92)' : 'rgba(15,23,42,0.85)';
    ctx.beginPath();
    ctx.roundRect(x - bw / 2, y + 3, bw, 17, 8);
    ctx.fill();
    ctx.fillStyle = light ? '#1e293b' : '#e2e8f0';
    ctx.textAlign = 'center';
    ctx.fillText(text, x, y + 15);
  }
}
